import { useMemo } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useEntraIdentity } from '@hooks/useEntraIdentity';
import { CampaignProvider } from '@hooks/useCampaign';
import { Header, type HeaderUser } from '@components/layout/Header';
import { Nav, type NavGroup } from '@components/layout/Nav';
import { Footer } from '@components/layout/Footer';
import { ClientNotice } from '@components/layout/ClientNotice';
import { campaignDisplay, resolveFromLocation } from '@lib/client-preselect';
import { clientLabel } from '@lib/client-key';

/**
 * AppShell — ClariCommand editorial frame for the Lab Intake bench app.
 *
 * Dark-ink topbar (brand + signed-in identity) over the paper body: the
 * flight-deck left nav (router-wired, collapses to an icon strip on the bench
 * phone) beside the routed page (<Outlet/>), with the editorial footer beneath.
 *
 * The shell is also where the campaign is decided, ONCE per location, from the Workflow Hub
 * `?client=` read (lib/client-preselect.ts) and handed to every page through CampaignProvider.
 * The shell sits inside AuthRoute, so the read is only ever applied for a signed-in user whom the
 * API's allowlist has admitted; it preselects a campaign the bench is configured to serve and
 * nothing else, and the header echoes the plan and campaign actually landed on.
 */

/**
 * The navigation table. Exported ONLY so a test can assert every target is an internal literal path.
 *
 * Nav.tsx navigates with `navigate(item.to)`, which is the one call in this app that does not take a
 * string literal at its call site, and the installed router carries an open-redirect advisory for a
 * navigation target that begins with a backslash or a scheme. Neither is reachable while these five
 * entries are the only things that reach it, so the test pins that rather than the advisory.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Resulting',
    items: [
      { id: 'bench', num: '01', name: 'Bench', meta: 'scan', to: '/' },
      { id: 'testing', num: '02', name: 'Testing', meta: 'batch', to: '/testing' },
      { id: 'review', num: '03', name: 'Review', meta: 'QA', to: '/review' },
    ],
  },
  {
    label: 'Records',
    items: [
      { id: 'archive', num: '04', name: 'Archive', meta: 'sent', to: '/archive' },
      { id: 'turnaround', num: '05', name: 'Turnaround', meta: 'TAT', to: '/turnaround' },
    ],
  },
];

export function AppShell() {
  const identity = useEntraIdentity();
  const location = useLocation();
  const preselect = useMemo(() => resolveFromLocation(location.search), [location.search]);
  const user: HeaderUser | undefined = identity
    ? {
        name: identity.name,
        role: 'CDX Lab',
        initials: identity.initials,
        email: identity.email,
      }
    : undefined;

  const planValue =
    preselect.source === 'hub'
      ? `${clientLabel(preselect.client)} · from the Hub`
      : clientLabel(preselect.client);

  return (
    <CampaignProvider value={preselect}>
      <div
        className="app-root"
        style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--paper)' }}
      >
        <Header
          brand={{ mark: 'LI', name: 'ClariDynamics', nameEm: 'Lab Intake' }}
          meta={[
            { label: 'Campaign', value: campaignDisplay(preselect.campaign) },
            { label: 'Plan', value: planValue },
          ]}
          user={user}
        />

        <ClientNotice notice={preselect.notice} />

        <div className="app-body" style={{ display: 'flex', flex: '1 1 auto', minHeight: 0 }}>
          <Nav groups={NAV_GROUPS} />
          <main
            id="main-content"
            tabIndex={-1}
            className="app-main"
            style={{ flex: '1 1 auto', minWidth: 0, overflowX: 'auto', background: 'var(--paper)' }}
          >
            <Outlet />
          </main>
        </div>

        <Footer left="ClariDynamics · Lab Intake" right="Internal · CDX only" />
      </div>
    </CampaignProvider>
  );
}
